import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LogService } from '@services/util/log.service';
import { ChartModule } from 'primeng/chart';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { DailyChartData, DashboardChartDataService } from '../../services/dashboard-chart-data.service';

@Component({
  selector: 'app-app-usage-chart',
  imports: [CommonModule, TranslateModule, ChartModule],
  templateUrl: './app-usage-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppUsageChartComponent implements OnInit {
  private readonly log = inject(LogService);
  private readonly chartDataService = inject(DashboardChartDataService);
  private readonly translateService = inject(TranslateService);

  // Signals for reactive state management
  protected readonly isLoading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly chartData = signal<any>(null);
  protected readonly chartOptions = signal<any>(null);

  ngOnInit() {
    this.loadChartData();
    this.initChartOptions();
  }

  private loadChartData() {
    this.isLoading.set(true);
    this.error.set(null);

    this.chartDataService.getAppUsageData().subscribe({
      next: (dailyData) => {
        this.setupChartData(dailyData);
        this.isLoading.set(false);
      },
      error: (error) => {
        this.log.error('Error loading app usage chart data:', error);
        this.error.set(this.translateService.instant('dashboard.charts.appUsage.error'));
        this.isLoading.set(false);
      }
    });
  }

  private setupChartData(dailyData: DailyChartData[]) {
    const labels = dailyData.map((item) => item.date);
    const percentageData = dailyData.map((item) => item.percentage);
    const trendData = this.calculateTrendLine(percentageData);

    this.chartData.set({
      labels,
      datasets: [
        {
          label: this.translateService.instant('dashboard.charts.appUsage.datasetLabel'),
          data: percentageData,
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          tension: 0.4,
          fill: true
        },
        {
          label: 'Trend',
          data: trendData,
          borderColor: '#f59e0b',
          backgroundColor: 'transparent',
          borderDash: [5, 5],
          borderWidth: 2,
          pointRadius: 0,
          fill: false,
          tension: 0
        }
      ]
    });
  }

  private calculateTrendLine(data: number[]): number[] {
    const n = data.length;
    const x = Array.from({ length: n }, (_, i) => i);

    // Calculate linear regression: y = mx + b
    const sumX = x.reduce((a, b) => a + b, 0);
    const sumY = data.reduce((a, b) => a + b, 0);
    const sumXY = x.reduce((sum, xi, i) => sum + xi * data[i], 0);
    const sumXX = x.reduce((sum, xi) => sum + xi * xi, 0);

    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    return x.map((xi) => slope * xi + intercept);
  }

  private initChartOptions() {
    this.chartOptions.set({
      responsive: true,
      maintainAspectRatio: false,
      aspectRatio: 1.5, // Lower value = taller chart (default is ~2)
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          ticks: {
            callback: function (value: any) {
              return value + '%';
            }
          }
        }
      },
      plugins: {
        legend: {
          display: true,
          position: 'top'
        },
        tooltip: {
          callbacks: {
            label: function (context: any) {
              return context.dataset.label + ': ' + context.raw + '%';
            }
          }
        }
      }
    });
  }
}
